<script lang="ts">
    import ManageKanbanStatusesPage from '@Alternatives/AlternativesLists/Pages/ManageKanbanStatusesPage'
    export default ManageKanbanStatusesPage
</script>

<template>
    <div>
        <div id="StatusDependenciesViewerDialog" class="modal-mask" v-if="depViewer.opened === true">
            <div class="modal-wrapper">
                <div class="modal-dialog modal-xl modal-dialog-scrollable">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Status dependencies</h5>
                            <button type="button" class="close" @click="depViewer.close()">&times;</button>
                        </div>

                        <div class="modal-body">
                            <h4 v-if="!depViewer.rm">
                                <i class="fa fa-spinner fa-spin"></i> Checking dependencies...
                            </h4>
                            <div v-else>
                                <div class="alert alert-warning text-dark" role="alert">
                                    <p>The status '<strong>{{depViewer.rm.Name}}</strong>' is used in <strong>{{depViewer.rm.Count}}</strong> places.</p>
                                </div>

                                <div class="card mt-3" v-if="depViewer.rm.Reports.length > 0">
                                    <div class="card-header">
                                        Reports ({{depViewer.rm.Reports.length}})
                                    </div>
                                    <ul class="list-group list-group-flush">
                                        <li class="list-group-item" v-for="r in depViewer.rm.Reports" :key="r.Id">
                                            <a class="text-primary" target="_blank" v-sanitizedHref="url_ReportViewPage(r)">{{r.Name}}</a>
                                        </li>
                                    </ul>
                                </div>

                                <div class="card mt-3" v-if="depViewer.rm.Lists.length > 0">
                                    <div class="card-header">
                                        Lists ({{depViewer.rm.Lists.length}})
                                    </div>
                                    <ul class="list-group list-group-flush">
                                        <li class="list-group-item" v-for="l in depViewer.rm.Lists" :key="l.Id">
                                            <span>{{l.Name}}</span>
                                        </li>
                                    </ul>
                                </div>

                                <div class="card mt-3" v-if="depViewer.rm.Notifications.length > 0">
                                    <div class="card-header">
                                        Notifications ({{depViewer.rm.Notifications.length}})
                                    </div>
                                    <ul class="list-group list-group-flush">
                                        <li class="list-group-item" v-for="n in depViewer.rm.Notifications" :key="n.Id">
                                            <span>{{n.Name}}</span>
                                        </li>
                                    </ul>
                                </div>

                                <div class="card mt-3" v-if="depViewer.rm.Alternatives.length > 0">
                                    <div class="card-header">
                                        Alternatives ({{depViewer.rm.Alternatives.length}})
                                    </div>
                                    <ul class="list-group list-group-flush">
                                        <li class="list-group-item" v-for="a in depViewer.rm.Alternatives" :key="a.Id">
                                            <span>{{a.Name}}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div class="modal-footer">
                            <button @click="depViewer.close()" type="button" class="btn btn-outline-secondary">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div if="StatusDeleterDialog" class="modal-mask" v-if="statusDeleter.opened === true">
            <div class="modal-wrapper">
                <div class="modal-dialog modal-lg modal-dialog-scrollable">
                    <div class="modal-content">
                        <div class="modal-header" :class="{'bg-danger': (statusDeleter.rm && statusDeleter.rm.HasDependants === false)}">
                            <h5 class="modal-title">Delete status</h5>
                            <button type="button" class="close" @click="statusDeleter.close()">&times;</button>
                        </div>

                        <div class="modal-body">
                            <h4 v-if="!statusDeleter.rm">
                                <i class="fa fa-spinner fa-spin"></i> Checking dependencies...
                            </h4>
                            <div v-else>
                                <div v-if="statusDeleter.rm.HasDependants === true">
                                    <div class="alert alert-danger text-dark" role="alert">
                                        <h5 class="alert-heading">Cannot delete this status</h5>
                                        <p>
                                            The status '<strong>{{statusDeleter.rm.Name}}</strong>' is used in <strong>{{statusDeleter.rm.Count}}</strong> places.
                                            To delete this status, first remove it from where it is used.
                                        </p>
                                    </div>
                                </div>
                                <div v-else>
                                    <h4>Are you sure you want to delete this status?</h4>
                                    <p>You are about to delete '<strong>{{statusDeleter.rm.Name}}</strong>'. This can't be undone.</p>
                                </div>

                            </div>
                        </div>

                        <div class="modal-footer">
                            <button v-if="statusDeleter.rm?.HasDependants === false" type="button" class="btn btn-danger" @click="handleDeleteKanbanStatusCommand">Delete</button>
                            <button v-if="statusDeleter.rm?.HasDependants === true" type="button" class="btn btn-secondary" @click="showStatusDependenciesViewer(statusDeleter.rm.Id)">View dependencies</button>
                            <button type="button" class="btn btn-outline-secondary" @click="statusDeleter.close()">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <PortfolioOrDecisionNavigation :workspaceId="workspaceId" :projectId="projectId" />

        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Kanban statuses</h1>
                </div>
                <div class="col-lg-6 text-right">
                    <button type="button" class="btn btn-secondary" style="padding: 0.7em 2em;" @click="createStatus">Add status</button>
                    <button type="button" class="btn btn-success" style="padding: 0.7em 2em;" @click="handleUpdateKanbanStatusesCommand">Save changes</button>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 60px;" />

        <div class="container" v-if="rm">
            <div class="alert alert-danger" role="alert" v-if="AlternativeStatusesView.length === 0">
                <strong>No statuses</strong> - there must be at least one status.
            </div>

            <div class="alert alert-danger" role="alert" v-if="errors?.length">
                <p v-for="e in errors">
                    {{e.Error}}
                </p>
            </div>

            <table class="table table-bordered">

                <tr v-for="s in AlternativeStatusesView" :key="s.OrderValue">
                    <td style="width: 120px;">
                        <button type="button" @click="moveUp(s)" class="btn btn-outline-secondary" style="max-width: 40px; width: 40px;">
                            <i class="fa fa-long-arrow-up" aria-hidden="true"></i>
                        </button>
                        <button type="button" @click="moveDown(s)" class="btn btn-outline-secondary" style="max-width: 40px; width: 40px;">
                            <i class="fa fa-long-arrow-down" aria-hidden="true"></i>
                        </button>
                    </td>
                    <td>
                        <div class="form-group m-0 p-0">
                            <input type="text" v-model.trim="s.Name" class="form-control">
                        </div>
                    </td>
                    <td style="width: 90px;">
                        <div class="dropdown">
                            <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                Actions
                            </button>
                            <div class="dropdown-menu dropdown-menu-right">
                                <a class="dropdown-item" v-if="s.Id" @click="showStatusDependenciesViewer(s.Id)">View dependencies</a>
                                <a class="dropdown-item" @click="deleteStatus(s)">Delete</a>
                            </div>
                        </div>
                    </td>
                </tr>

            </table>


        </div>


    </div>
</template>

<style scoped>

</style>


